import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PencilSimple, Plus, Trash, FloppyDisk, X } from "@phosphor-icons/react";
import { useContent } from "@/components/Editable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BigfootHistory() {
  const { unlocked, adminKey, openSignIn } = useContent();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ title: "", year: "", body: "" });
  const [creating, setCreating] = useState(false);

  const headers = adminKey ? { "X-Admin-Key": adminKey } : {};

  const load = async () => {
    try {
      const r = await axios.get(`${API}/bigfoot-history`);
      setSections(r.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Could not load the chronicle.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (s) => {
    setCreating(false);
    setEditingId(s.id);
    setEditDraft({ title: s.title, year: s.year || "", body: s.body });
  };

  const startCreate = () => {
    setEditingId(null);
    setCreating(true);
    setEditDraft({ title: "", year: "", body: "" });
  };

  const cancelEdit = () => { setEditingId(null); setCreating(false); };

  const saveEdit = async () => {
    if (!editDraft.title.trim() || !editDraft.body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    try {
      if (creating) {
        const r = await axios.post(`${API}/bigfoot-history`, editDraft, { headers });
        setSections((prev) => [...prev, r.data]);
        toast.success("Entry added to the chronicle.");
      } else {
        const r = await axios.patch(`${API}/bigfoot-history/${editingId}`, editDraft, { headers });
        setSections((prev) => prev.map((s) => (s.id === editingId ? r.data : s)));
        toast.success("Entry updated.");
      }
      setEditingId(null);
      setCreating(false);
    } catch (e) {
      console.error(e);
      toast.error("Save failed — check your curator key.");
    }
  };

  const deleteSection = async (id) => {
    if (!window.confirm("Delete this entry from the chronicle?")) return;
    try {
      await axios.delete(`${API}/bigfoot-history/${id}`, { headers });
      setSections((prev) => prev.filter((s) => s.id !== id));
      toast.success("Entry removed.");
    } catch (e) {
      console.error(e);
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="parchment-bg" data-testid="bigfoot-history-page">
      <section className="max-w-5xl mx-auto px-5 md:px-8 pt-20 md:pt-28">
        <div className="flex items-center justify-between">
          <div className="font-mono-label text-[11px] text-[#6B5A3C]">
            § Chronicle · An Ongoing Record
          </div>
          {!unlocked && (
            <button
              onClick={openSignIn}
              data-testid="unlock-btn"
              className="font-mono-label text-[10px] text-[#6B5A3C] hover:text-[#3A2E1A]"
            >
              Curator sign-in
            </button>
          )}
        </div>

        <h1 className="font-serif-display text-6xl md:text-8xl text-[#3A2E1A] mt-4 leading-[0.95]">
          The History of <em>Bigfoot</em>
          <br />in New Mexico.
        </h1>

        <p className="mt-8 max-w-2xl text-lg text-[#4A3F2B] leading-relaxed">
          A working chronicle of how the figure now called Bigfoot has been
          described, recorded, and remembered in this corner of the Southwest.
          Drawn from oral tradition, newspaper archives, and modern sighting reports.
        </p>

        <div className="parchment-rule mt-10" />
      </section>

      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-14 pb-16">
        {loading ? (
          <div className="text-[#6B5A3C] font-mono-label text-[11px]">Loading the chronicle…</div>
        ) : (
          <div className="space-y-16">
            {sections.map((s, idx) => (
              <article key={s.id} data-testid={`history-section-${s.id}`} className="relative">
                <div className="grid md:grid-cols-12 gap-8">
                  <div className="md:col-span-3">
                    <div className="font-mono-label text-[10px] text-[#8C6A2E]">
                      Chapter {(idx + 1).toString().padStart(2, "0")}
                    </div>
                    {s.year && (
                      <div className="font-serif-display text-3xl text-[#3A2E1A] mt-2 italic">{s.year}</div>
                    )}
                  </div>

                  <div className="md:col-span-9">
                    {editingId === s.id ? (
                      <EditForm draft={editDraft} setDraft={setEditDraft} onSave={saveEdit} onCancel={cancelEdit} />
                    ) : (
                      <>
                        <h2 className="font-serif-display text-4xl md:text-5xl text-[#3A2E1A] leading-tight">
                          {s.title}
                        </h2>
                        <p className="mt-5 text-[#3A2E1A]/85 text-lg leading-[1.75] whitespace-pre-wrap font-serif-display">
                          {s.body}
                        </p>
                        {unlocked && (
                          <div className="mt-4 flex gap-3">
                            <button onClick={() => startEdit(s)} data-testid={`edit-${s.id}`} className="parchment-btn">
                              <PencilSimple size={14} /> Edit
                            </button>
                            <button onClick={() => deleteSection(s.id)} data-testid={`delete-${s.id}`} className="parchment-btn danger">
                              <Trash size={14} /> Delete
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="parchment-rule mt-12" />
              </article>
            ))}

            {unlocked && (
              <div className="grid md:grid-cols-12 gap-8">
                <div className="md:col-span-3" />
                <div className="md:col-span-9">
                  {creating ? (
                    <EditForm draft={editDraft} setDraft={setEditDraft} onSave={saveEdit} onCancel={cancelEdit} isNew />
                  ) : (
                    <button onClick={startCreate} data-testid="add-section-btn" className="parchment-btn primary">
                      <Plus size={14} /> Add Chapter
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function EditForm({ draft, setDraft, onSave, onCancel, isNew }) {
  return (
    <div className="bg-[#F0E2C2]/60 border border-[#C9B68A] p-6" data-testid={isNew ? "create-form" : "edit-form"}>
      <div className="font-mono-label text-[10px] text-[#8C6A2E]">
        {isNew ? "New chapter" : "Editing chapter"}
      </div>
      <input
        type="text"
        value={draft.title}
        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        placeholder="Chapter title"
        data-testid="draft-title"
        className="mt-3 w-full bg-transparent border-b border-[#C9B68A] py-2 text-2xl font-serif-display text-[#3A2E1A] focus:border-[#3A2E1A] outline-none"
      />
      <input
        type="text"
        value={draft.year}
        onChange={(e) => setDraft({ ...draft, year: e.target.value })}
        placeholder="Year (e.g. 1970s, Pre-1958)"
        data-testid="draft-year"
        className="mt-4 w-full bg-transparent border-b border-[#C9B68A] py-2 text-base text-[#3A2E1A] focus:border-[#3A2E1A] outline-none"
      />
      <textarea
        value={draft.body}
        onChange={(e) => setDraft({ ...draft, body: e.target.value })}
        placeholder="Write the chapter…"
        rows={8}
        data-testid="draft-body"
        className="mt-4 w-full bg-transparent border border-[#C9B68A] p-3 text-base text-[#3A2E1A] focus:border-[#3A2E1A] outline-none leading-relaxed"
      />
      <div className="mt-5 flex gap-3">
        <button onClick={onSave} data-testid="save-btn" className="parchment-btn primary">
          <FloppyDisk size={14} /> Save
        </button>
        <button onClick={onCancel} data-testid="cancel-btn" className="parchment-btn">
          <X size={14} /> Cancel
        </button>
      </div>
    </div>
  );
}
